import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client", age: "", phone: "", province: "", city: "", suburb: "", whatsapp_opt_in: true });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Best-effort auto-fill of province/city/suburb from browser geolocation on first render.
  // Non-blocking — user can override any field manually. Requires HTTPS + user consent.
  const [geoBusy, setGeoBusy] = useState(false);
  const detectGeo = () => {
    if (geoBusy || !("geolocation" in navigator)) return;
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=jsonv2&zoom=14&accept-language=en`, { headers: { Accept: "application/json" } });
          const j = await r.json();
          const a = j.address || {};
          setForm((p) => ({
            ...p,
            province: p.province || a.state || a.region || "",
            city: p.city || a.city || a.town || a.village || a.municipality || "",
            suburb: p.suburb || a.suburb || a.neighbourhood || a.residential || "",
          }));
        } catch (err) { logger.warn("Reverse-geocode failed:", err?.message || err); }
        finally { setGeoBusy(false); }
      },
      () => setGeoBusy(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.province || !form.city || !form.suburb) {
      setError("Province, city and suburb are all required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, age: form.age ? Number(form.age) : undefined, phone: form.phone || undefined, whatsapp_opt_in: !!form.whatsapp_opt_in };
      await register(payload);
      toast.success("Welcome to Hellofoundation Youth Mental Wellness & Support!");
      nav("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const roles = [
    { id: "client", label: "I'm a young person", help: "13–24, looking for support" },
    { id: "parent", label: "I'm a parent / caregiver", help: "Looking for guidance" },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="brand-card p-8 sm:p-10">
          <div className="overline mb-3">Create your account</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#3D405B] tracking-tight">Welcome — let&apos;s begin.</h1>
          <p className="text-sm text-[#7A7D96] mt-2">Free, confidential, and 100% yours. We never share your details.</p>

          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full mt-6 flex items-center justify-center gap-3 px-5 py-3 rounded-2xl border-2 border-[#3D405B]/15 hover:bg-[#3D405B]/5 transition-all font-semibold text-[#3D405B]"
            data-testid="register-google-btn"
          >
            <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 5.1 29 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.5 0 20-7.6 20-21 0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14l6.6 4.8C14.7 14.4 19 11 24 11c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 5.1 29 3 24 3 16.3 3 9.6 7.4 6.3 14z"/><path fill="#4CAF50" d="M24 45c5 0 9.5-1.9 13-5l-6-5.1c-2 1.4-4.4 2.1-7 2.1-5.3 0-9.7-3.4-11.3-8L6 33.5C9.2 40.4 16 45 24 45z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4-3.9 5.4l6 5.1c-.4.4 6.6-4.9 6.6-14.5 0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#3D405B]/10" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#7A7D96] font-bold">or use email</span>
            <div className="flex-1 h-px bg-[#3D405B]/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-5 mt-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {roles.map((r) => (
                <label key={r.id} className={`brand-card p-4 cursor-pointer transition-all ${form.role === r.id ? "ring-2 ring-primary shadow-md" : ""}`} data-testid={`register-role-${r.id}`}>
                  <input type="radio" name="role" value={r.id} checked={form.role === r.id} onChange={() => setForm({ ...form, role: r.id })} className="sr-only" />
                  <div className="font-bold text-[#3D405B]">{r.label}</div>
                  <div className="text-xs text-[#7A7D96] mt-0.5">{r.help}</div>
                </label>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Full name<span className="text-primary ml-0.5" aria-label="required">*</span></label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none" data-testid="register-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Age (optional)</label>
                <input type="number" min="6" max="99" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none" data-testid="register-age-input" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Email<span className="text-primary ml-0.5" aria-label="required">*</span></label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none" data-testid="register-email-input" />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Phone (optional)</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+27821234567" className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none" data-testid="register-phone-input" />
              <div className="text-xs text-[#7A7D96] mt-1">International format if you&apos;d like WhatsApp reminders.</div>
            </div>

            <div className="p-4 rounded-2xl bg-secondary/5 border border-secondary/20 space-y-3" data-testid="register-geo-block">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-bold text-[#3D405B] uppercase tracking-wider">Where are you based? <span className="text-primary" aria-label="required">*</span></div>
                <button type="button" onClick={detectGeo} disabled={geoBusy} className="text-xs font-bold text-secondary hover:underline disabled:opacity-50" data-testid="register-geo-detect">
                  {geoBusy ? "Detecting…" : "Use my location"}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-[#7A7D96] uppercase tracking-wider mb-1">Province <span className="text-primary">*</span></label>
                  <select required value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="w-full px-3 py-2.5 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none text-sm" data-testid="register-province">
                    <option value="">Select…</option>
                    {["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State", "Limpopo", "Mpumalanga", "North West", "Northern Cape"].map((p) => (<option key={p} value={p}>{p}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#7A7D96] uppercase tracking-wider mb-1">City <span className="text-primary">*</span></label>
                  <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Johannesburg" className="w-full px-3 py-2.5 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none text-sm" data-testid="register-city" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#7A7D96] uppercase tracking-wider mb-1">Suburb <span className="text-primary">*</span></label>
                  <input required value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} placeholder="Auto-detected — confirm" className="w-full px-3 py-2.5 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none text-sm" data-testid="register-suburb" />
                </div>
              </div>
              <div className="text-[11px] text-[#7A7D96]">Helps us match you with counsellors nearby — you can update this any time in your profile.</div>
            </div>

            <label className="flex items-start gap-3 p-4 rounded-2xl bg-accent/15 border border-accent/40 cursor-pointer" data-testid="register-whatsapp-optin-label">
              <input
                type="checkbox"
                checked={form.whatsapp_opt_in}
                onChange={(e) => setForm({ ...form, whatsapp_opt_in: e.target.checked })}
                className="mt-1 w-5 h-5 accent-primary rounded"
                data-testid="register-whatsapp-optin"
              />
              <span className="text-sm text-[#3D405B]">
                <span className="font-bold">Send me WhatsApp reminders</span> before my sessions so I never miss one.
                <span className="block text-xs text-[#7A7D96] mt-1">You can turn this off any time in your profile or by replying STOP.</span>
              </span>
            </label>

            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Password<span className="text-primary ml-0.5" aria-label="required">*</span></label>
              <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none" data-testid="register-password-input" />
              <div className="text-xs text-[#7A7D96] mt-1">At least 6 characters.</div>
            </div>

            {error && <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-xl" data-testid="register-error">{error}</div>}

            <button type="submit" disabled={submitting} className="w-full px-5 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60" data-testid="register-submit-btn">
              {submitting ? "Creating account…" : "Create account"}
            </button>

            <p className="text-sm text-center text-[#7A7D96]">
              Already have an account? <Link to="/login" className="text-primary font-bold hover:underline" data-testid="register-login-link">Sign in</Link>
            </p>
            <p className="text-xs text-center text-[#7A7D96] pt-2 border-t border-[#3D405B]/5">
              Registered counsellor? <Link to="/counsellor-apply" className="text-secondary font-bold hover:underline" data-testid="register-counsellor-apply-link">Volunteer with us →</Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
